import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, getIn } from 'formik';

import { USER_ROLE_RENTER } from 'common/userRoles';
import slugify from 'common/util/slugify';
import makeFullName from 'common/util/makeFullName';
import getApplicationFormFields, {
  APPLICATION_FORM_TYPE_RENTER_PROFILE,
  APPLICATION_FORM_TYPE_GUARANTOR_APPLICATION,
} from 'common/householdApplication/applicationFormFields';

import {
  HOUSEHOLD_APPLICATION_CATEGORY_GUARANTOR_APPLICATION,
  HOUSEHOLD_APPLICATION_CATEGORY_HOUSEHOLD_APPLICATION,
} from 'common/householdConstants';

import {
  getHouseholdApplicationDataFields,
} from 'common/householdApplication/applicationHelpers';

import {
  SR_API_ACTION_HOUSEHOLD_GET_RENTERS,
  SR_API_ACTION_HOUSEHOLD_GET_RENTER_PROFILE,
  SR_API_ACTION_HOUSEHOLD_GET_APPLICATION,
  SR_API_ACTION_ADMIN_UPDATE_ITEM,
} from 'api/actionTypes';

import {
  validateFormField,
  createInitialValues,
  createFormValidationFn,
  getFormValueDisplay,
} from 'client/helpers/formikHelpers';

import {
  SCREEN_ID_APPLICATION_FORM_CONFIRMATION,
} from 'client/constants/screenIds';

import branch from 'client/core/branch';
import log from 'client/lib/log';
import { makeProxiedS3FilePath } from 'client/lib/fileUploader';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withApiState from 'client/helpers/withApiState';
import allowedRoles from 'client/helpers/allowedRoles';
import HouseholdActions from 'client/actions/Household';
import postWithState from 'client/actions/postWithState';

import SrLink from 'client/components/SrLink/SrLink';
import Navbar from 'client/components/Navbar';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrFormikField from 'client/components/SrFormikField';
import SrBox from 'client/components/SrBox';
import SrSeparator from 'client/components/SrSeparator';
import SrHouseholdApplicationPhotoFieldViewer from 'client/components/SrHouseholdApplicationPhotoFieldViewer';

const debug = log.debug('sunroom:pages:ApplicationFormConfirmation');

const SrHouseholdAdditionalDocsForm = ({ onSubmit, isBusy, filePath, uploadAs }) => (
  <Formik
    enableReinitialize
    initialValues={{
      additionalDocs: [],
    }}
    onSubmit={onSubmit}
    render={(formikBag) => (
      <Form>
        <SrFormikField
          formikBag={formikBag}
          formField={{
            id: 'additionalDocs',
            label: '',
            validations: ['required'],
            type: 'file_multi',
            properties: {
              fileAccept: '*',
              filePath: filePath,
              isPrivate: true,
              uploadAs: uploadAs,
            },
          }}
        />
        <ButtonDeprecated type='submit' disabled={isBusy}>
          {isBusy ? 'uploading...' : 'upload'}
        </ButtonDeprecated>
      </Form>
    )}
  />
);

SrHouseholdAdditionalDocsForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isBusy: PropTypes.bool.isRequired,
  uploadAs: PropTypes.number.isRequired,
};

SrHouseholdAdditionalDocsForm.displayName = 'SrHouseholdAdditionalDocsForm';

@withTransitionHook
@allowedRoles({
  roles: [
    USER_ROLE_RENTER,
  ],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@branch({
  renterProfilesByUserId: ['household', 'renterProfilesByUserId'],
  householdRentersByUserId: ['household', 'rentersByUserId'],
  householdApplicationsById: ['household', 'applicationsById'],
  householdsById: ['household', 'byId'],
  appUrl: ['env', 'APP_URL'],
})
@withApiState(({ match }) => ({
  getRenterProfileApiState: {
    action: SR_API_ACTION_HOUSEHOLD_GET_RENTER_PROFILE,
    queryId: match.params.userId,
  },
  getHouseholdRentersApiState: {
    action: SR_API_ACTION_HOUSEHOLD_GET_RENTERS,
    queryId: match.params.userId,
  },
  getHouseholdApplicationApiState: {
    action: SR_API_ACTION_HOUSEHOLD_GET_APPLICATION,
    queryId: match.params.applicationId,
  },
  updateAdditionalDocsApiState: {
    action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
    queryId: 'additionalDocuments',
  },
  updateAdditionalProofOfIncomeApiState: {
    action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
    queryId: 'additionalProofOfIncomePhotos',
  },
}))
@withScrollToTop
@withScreenId(SCREEN_ID_APPLICATION_FORM_CONFIRMATION)
class ApplicationFormEditPage extends Component {
  static fetchData(match, inject) {
    if (match.params.applicationId) {
      return Promise.all([
        HouseholdActions.loadHouseholdApplication(inject, {
          applicationId: parseInt(match.params.applicationId, 10),
        }),
        HouseholdActions.loadHouseholdRenters(inject, {
          userId: parseInt(match.params.userId, 10),
        }),
        HouseholdActions.loadByApplicationId(inject, {
          applicationId: parseInt(match.params.applicationId, 10),
        }),
        HouseholdActions.loadRenterProfile(inject, {
          userId: parseInt(match.params.userId, 10),
        }),
      ]);
    }
    return Promise.all([
      HouseholdActions.loadRenterProfile(inject, {
        userId: parseInt(match.params.userId, 10),
      }),
      HouseholdActions.loadHouseholdRenters(inject, {
        userId: parseInt(match.params.userId, 10),
      }),
    ]);
  }

  static getPageMetadata = () => ({
    pageTitle: 'Sunroom - Edit Application',
  })

  static propTypes = {
    renterProfilesByUserId: PropTypes.object.isRequired,
    householdRentersByUserId: PropTypes.object.isRequired,
    householdApplicationsById: PropTypes.object,
    householdsById: PropTypes.object.isRequired,
    getRenterProfileApiState: PropTypes.object.isRequired,
    getHouseholdRentersApiState: PropTypes.object.isRequired,
    getHouseholdApplicationApiState: PropTypes.object.isRequired,
    updateAdditionalDocsApiState: PropTypes.object.isRequired,
    updateAdditionalProofOfIncomeApiState: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    appUrl: PropTypes.string.isRequired,
  }

  _getHousehold = (props) => {
    const {
      householdsById,
      householdApplicationsById,
      match: {
        params,
      },
    } = props;

    const application = householdApplicationsById[params.applicationId];
    if (!application) {
      return null;
    }
    return householdsById[application.householdId];
  }

  _onAdditionalDocRemove = (additionalDocToRemove) => {
    const {
      match: { params },
      renterProfilesByUserId,
    } = this.props;
    const userId = parseInt(params.userId, 10);
    const additionalDocuments = renterProfilesByUserId[userId].additionalDocuments || [];

    const newAdditionalDocuments = additionalDocuments.filter((doc) => {
      return doc !== additionalDocToRemove;
    });
    this._updateHouseholdAdditionalDocs({
      additionalDocuments: newAdditionalDocuments,
    });
  }

  _onAdditionalDocsFormSubmit = ({ additionalDocs }) => {
    const {
      match: { params },
      renterProfilesByUserId,
    } = this.props;
    const userId = parseInt(params.userId, 10);

    if (additionalDocs.length > 0) {
      const newAdditionalDocuments = [
        ...(renterProfilesByUserId[userId].additionalDocuments || []),
        ...additionalDocs,
      ];

      this._updateHouseholdAdditionalDocs({
        additionalDocuments: newAdditionalDocuments,
      })
        .catch((error) => {
          log.error(error);
        });
    }
  }

  _updateHouseholdAdditionalDocs = ({ additionalDocuments }) => {
    const {
      dispatch,
      match: { params },
    } = this.props;
    const userId = parseInt(params.userId, 10);
    return dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      queryId: 'additionalDocuments',
      payload: {
        table: 'householdApplications',
        where: {
          category: 'renterProfile',
          userId: userId,
        },
        updates: {
          additionalDocuments,
        },
      },
    }).then(() => {
      return dispatch(HouseholdActions.loadRenterProfile, {
        userId: userId,
        forceRefresh: true,
      });
    }).catch((error) => {
      log.error(error);
    });

  }

  _onUpdatePhotoField = ({ application, photoFieldId, photoFieldValue }) => {
    const { dispatch, match: { params } } = this.props;
    const userId = parseInt(params.userId, 10);
    const listingId = parseInt(params.listingId, 10);
    dispatch(HouseholdActions.updateRenterProfile, {
      userId: userId,
      updates: {
        [photoFieldId]: photoFieldValue,
        ...photoFieldId === 'additionalProofOfIncomePhotos' && {
          additionalProofOfIncomePhotosCount: photoFieldValue.length,
        },
      },
    }).then((renterProfile) => {
      if (application.category === HOUSEHOLD_APPLICATION_CATEGORY_HOUSEHOLD_APPLICATION) {
        return dispatch(HouseholdActions.submitHouseholdApplication, {
          userId: userId,
          listingId: listingId,
          householdApplication: renterProfile,
        });
      }
      if (application.category === HOUSEHOLD_APPLICATION_CATEGORY_GUARANTOR_APPLICATION) {
        return dispatch(HouseholdActions.submitGuarantorApplication, {
          userId: userId,
          listingId: listingId,
          householdId: application.householdId,
          guarantorApplication: renterProfile,
        });
      }
    }).then(() => {
      return dispatch(HouseholdActions.loadHouseholdApplication, {
        applicationId: application.id,
        forceRefresh: true,
      });
    }).catch((error) => {
      log.error(error);
    });
  }

  render() {
    const {
      renterProfilesByUserId,
      householdRentersByUserId,
      householdApplicationsById,
      householdsById,
      getRenterProfileApiState,
      getHouseholdRentersApiState,
      getHouseholdApplicationApiState,
      updateAdditionalDocsApiState,
      updateAdditionalProofOfIncomeApiState,
      appUrl,
      match: {
        params,
      },
    } = this.props;

    const userId = parseInt(params.userId, 10);
    const listingId = parseInt(params.listingId, 10);
    const applicationId = parseInt(params.applicationId, 10);

    const householdApplication = applicationId
      ? householdApplicationsById[applicationId]
      : renterProfilesByUserId[userId];

    const isLoading = (
      getRenterProfileApiState.inProgress
      || getHouseholdApplicationApiState.inProgress
      || getHouseholdRentersApiState.inProgress
      || !householdApplication
    );

    const apiError = (
      getRenterProfileApiState.error
      || getHouseholdRentersApiState.error
    );

    if (isLoading) {
      return (
        <div className='loading-gif clearfix' />
      );
    }

    const household = householdsById[householdApplication.householdId];
    const userName = makeFullName(householdApplication.firstName, householdApplication.lastName);
    const isLeadRenter = household?.leadRenterId === userId;
    const renterProfile = renterProfilesByUserId[userId];

    const roommates = (householdRentersByUserId[userId] || []).filter(({ id }) => {
      return id !== userId;
    });

    const formType = householdApplication.category === HOUSEHOLD_APPLICATION_CATEGORY_GUARANTOR_APPLICATION
      ? APPLICATION_FORM_TYPE_GUARANTOR_APPLICATION
      : APPLICATION_FORM_TYPE_RENTER_PROFILE;

    const formFields = getApplicationFormFields({
      formValues: getHouseholdApplicationDataFields(householdApplication),
      roommates: roommates,
      formType: formType,
      isLeadRenter,
    });
    const formValues = createInitialValues({
      formFields: formFields,
      defaultValues: getHouseholdApplicationDataFields(householdApplication),
    });
    const formIsValid = createFormValidationFn(formFields)(formValues);

    let formFieldItems = formFields.map((formField) => {
      const editLinkUrl = (
        `/application/${userId}/${listingId}/form` +
        (applicationId ? `/${encodeURIComponent(applicationId)}` : '') +
        `?fieldId=${encodeURIComponent(formField.id)}` +
        `&editMode=field&formType=${formType}`
      );
      const formFieldValue = getIn(formValues, formField.id);
      const error = validateFormField({
        validations: formField.validations,
        value: formFieldValue,
        values: formValues,
      });

      return {
        formField,
        formFieldValue,
        editLinkUrl,
        error,
      };
    });
    formFieldItems = [
      ...formFieldItems.filter(({ error }) => error),
      ...formFieldItems.filter(({ error }) => !error),
    ];

    debug('render', {
      isLoading,
      householdApplication,
      formFields,
      formValues,
      formIsValid,
    });

    return (
      <div className='page-applicationFormEdit'>
        <Navbar
          leftButton={
            <SrLink
              className='back link'
              to={`/admin/household/all/${householdApplication.userId}`}
            />
          }
        />
        {apiError && (
          <div>
            {apiError.message}
          </div>
        )}
        {household && (
          <div>
            <span className='title bold'>Upload additional documents</span>
            {(renterProfilesByUserId[userId].additionalDocuments || []).map((documentLink) => (
              <div className='photoContainer' key={documentLink}>
                <div
                  className='photoActionContainer'
                  onClick={() => {
                    this._onAdditionalDocRemove(documentLink);
                  }}
                >
                  <span className='button white small-admin'>
                    delete
                  </span>
                </div>
                <div
                  className='uploadedThumbnail'
                  style={{ backgroundImage: `url("${makeProxiedS3FilePath(documentLink, appUrl)}")` }}
                />
                <a
                  rel='noopener noreferrer'
                  target='_blank'
                  href={makeProxiedS3FilePath(documentLink, appUrl)}
                >
                  <span className='bold'>Download: </span>
                  <span className='blue-bright'>{makeProxiedS3FilePath(documentLink, appUrl)}</span>
                </a>
              </div>
            ))}
            <SrHouseholdAdditionalDocsForm
              onSubmit={this._onAdditionalDocsFormSubmit}
              isBusy={updateAdditionalDocsApiState.inProgress}
              // eslint-disable-next-line max-len
              filePath={`/renter-application-uploads/${slugify(userName)}-add-doc-${(renterProfile.additionalDocuments || []).length + 1}-{uuid}`}
              uploadAs={userId}
            />

            <SrBox padding='80px 0'>
              <SrSeparator />
            </SrBox>

            <SrHouseholdApplicationPhotoFieldViewer
              title='Additional Proof of Income'
              application={householdApplicationsById[applicationId]}
              photoFieldId='additionalProofOfIncomePhotos'
              // eslint-disable-next-line max-len
              filePath={`/renter-application-uploads/${slugify(userName)}-add-poi-${(renterProfile.additionalProofOfIncomePhotos || []).length + 1}-{uuid}`}
              onUpdate={this._onUpdatePhotoField}
              isBusy={updateAdditionalProofOfIncomeApiState.inProgress}
            />
          </div>
        )}
        {formFieldItems.map(({ formField, formFieldValue, editLinkUrl, error }) => (
          <div key={formField.id} className={error ? 'questionContainer invalid' : 'questionContainer'}>
            <span className={`title bold ${error ? 'color-red' : ''}`}>{formField.title}</span>
            <span className='answer'>
              {formField.type === 'file_single' ? (
                <div>
                  <div
                    className='uploadedThumbnail'
                    style={{ backgroundImage: `url("${makeProxiedS3FilePath(formFieldValue, appUrl)}")` }}
                  />
                  {formFieldValue ? (
                    <a
                      rel='noopener noreferrer'
                      target='_blank'
                      href={makeProxiedS3FilePath(formFieldValue, appUrl)}
                    >
                      {makeProxiedS3FilePath(formFieldValue, appUrl)}
                    </a>
                  ) : (
                    <span>--</span>
                  )}
                  <SrLink to={editLinkUrl}>
                    <span className='editLink'>
                      Edit
                    </span>
                  </SrLink>
                </div>
              ) : (
                <div>
                  {getFormValueDisplay({ formField, value: formFieldValue })}
                  <SrLink to={editLinkUrl}>
                    <span className='editLink'>
                      Edit
                    </span>
                  </SrLink>
                </div>
              )}
            </span>
          </div>
        ))}
      </div>
    );
  }
}

ApplicationFormEditPage.displayName = 'ApplicationFormEditPage';

export default ApplicationFormEditPage;
